import { FilterFieldType, IFilter } from './types'

export const FULLY_DILUTED_OWNERSHIP_QUERY = `CASE WHEN COALESCE(MAX("capTableDetails"."fully_diluted_ownership"), 0) = 0 THEN CAST(SUM("capTableDetails"."diluted_shares") AS numeric) / NULLIF(MAX("capTableSummary".total_fully_diluted_shares), 0) ELSE MAX("capTableDetails"."fully_diluted_ownership") END`
export const OUTSTANDING_OWNERSHIP_QUERY = `CAST(SUM("capTableDetails"."outstanding_shares") AS numeric) / NULLIF(MAX("capTableSummary".total_outstanding_shares), 0)`

export const CAP_TABLE_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Investor',
    title: 'Investor',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Mapping',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Prediction',
    title: 'Prediction',
  },
  {
    field: 'currentOutstandingOwnership',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Current Outstanding Ownership',
  },
  {
    field: 'percentOfRound',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Round',
  },
  {
    field: 'percentAfterRound',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% After Round',
  },
  {
    field: 'amountInvested',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
  },
  {
    field: 'roundInvested',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Round',
    title: 'Round',
  },
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Ignore',
  },
] as const satisfies IFilter[]

export const CAP_TABLE_SUMMARY_TITLE_FILTERS_CONFIG = [
  {
    field: 'action',
    title: '',
    type: FilterFieldType.Empty,
  },
  {
    field: 'linked_name',
    alias: 'alias',
    type: FilterFieldType.Text,
    title: 'Investor',
    isSortable: true,
    align: 'left',
  },
] as const satisfies IFilter[]

export const CAP_TABLE_SUMMARY_DATA_FILTERS_CONFIG = [
  {
    field: 'totalInvestment',
    type: FilterFieldType.Text,
    title: 'Total Investment',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: 'SUM("capTableDetails"."outstanding_shares" * "capTableDetails"."price_per_share")',
  },
  {
    field: 'outstandingShares',
    type: FilterFieldType.Text,
    title: 'Outstanding Ownership',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: OUTSTANDING_OWNERSHIP_QUERY,
  },
  {
    field: 'fullyDilutedOwnership',
    type: FilterFieldType.Percent,
    title: 'Fully Diluted Ownership',
    align: 'left',
    aggColumn: true,
    isSortable: true,
    dbField: FULLY_DILUTED_OWNERSHIP_QUERY,
  },
] as const satisfies IFilter[]

export const CAP_TABLE_MAPPING_FILTERS_CONFIG = [
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Description',
    align: 'left',
    alias: 'capTableDetails',
  },
  {
    field: 'fullyDilutedOwnership',
    type: FilterFieldType.Percent,
    title: 'Fully Diluted Ownership',
    align: 'left',
    aggColumn: true,
    isSortable: true,
    dbField: FULLY_DILUTED_OWNERSHIP_QUERY,
  },
  {
    field: 'linked_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    title: 'Investor',
    alias: 'alias',
    excludeFilter: true,
  },
  {
    field: 'investorPrediction',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Prediction',
    alias: 'capTableDetails',
    dbField: 'investor_prediction',
  },
  {
    field: 'boardSeats',
    alias: 'board_seats',
    excludeFullSearchFilter: true,
    dbField: 'name',
    type: FilterFieldType.List,
    title: 'Board Seat Holder(s)',
    isSortable: false,
  },
] as const satisfies IFilter[]

export const INVESTOR_SUMMARY_TITLE_FILTERS_CONFIG = [
  {
    field: 'action',
    title: 'Action',
    type: FilterFieldType.Empty,
  },
  {
    field: 'linked_name',
    alias: 'alias',
    type: FilterFieldType.Text,
    title: 'Investor',
    isSortable: true,
    align: 'left',
  },
] as const satisfies IFilter[]

export const INVESTOR_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'linked_name',
    alias: 'alias',
    type: FilterFieldType.Text,
    placeholder: 'Investor',
    title: 'Investor',
    isSortable: true,
  },
  {
    field: 'totalInvestment',
    dbField: 'SUM(investorDetails.outstanding_shares * investorDetails.price_per_share)',
    aggColumn: true,
    type: FilterFieldType.Amount,
    placeholder: 'Total Investment',
    title: 'Total Investment ($)',
    isSortable: true,
  },
  {
    field: 'averageInvestment',
    dbField:
      'SUM(investorDetails.outstanding_shares * investorDetails.price_per_share) / COUNT(DISTINCT investorDetails.cap_table_summary_id)',
    aggColumn: true,
    type: FilterFieldType.Amount,
    placeholder: 'Avg. Investment',
    title: 'Avg. Investment ($)',
    isSortable: true,
  },
  {
    field: 'avgOutstandingOwnership',
    dbField: 'AVG(sub."outstandingOwnership")',
    aggColumn: true,
    type: FilterFieldType.Percent,
    placeholder: 'Avg. Outstanding Ownership (%)',
    title: 'Avg. Outstanding Ownership (%)',
    isSortable: true,
  },
  {
    field: 'avgFullyDilutedOwnership',
    dbField: 'AVG(sub."fullyDilutedOwnership")',
    aggColumn: true,
    type: FilterFieldType.Percent,
    placeholder: 'Avg. Diluted Ownership (%)',
    title: 'Avg. Diluted Ownership (%)',
    isSortable: true,
  },
  {
    field: 'totalClients',
    dbField: 'COUNT(DISTINCT investorDetails.cap_table_summary_id)',
    aggColumn: true,
    type: FilterFieldType.Number,
    placeholder: 'Total Clients',
    title: 'Total Clients',
    isSortable: true,
  },
  {
    field: 'totalBoardSeats',
    dbField: 'COALESCE(MAX("boardSeats"."totalBoardSeats"), 0)',
    aggColumn: true,
    type: FilterFieldType.Number,
    placeholder: 'Total Board Seats',
    title: 'Total Board Seats',
    isSortable: true,
  },
] as const satisfies IFilter[]

export const INVESTOR_ENTITY_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'clientName',
    alias: 'capTableSummary',
    dbField: 'client_name',
    type: FilterFieldType.Text,
    placeholder: 'Client',
    title: 'Client',
    isSortable: true,
  },
  {
    field: 'totalInvestment',
    dbField: 'SUM(capTableDetails.outstandingShares * capTableDetails.pricePerShare)',
    aggColumn: true,
    type: FilterFieldType.Amount,
    placeholder: 'Total Investment',
    title: 'Total Investment',
    isSortable: true,
  },
  {
    field: 'outstandingOwnership',
    type: FilterFieldType.Percent,
    title: 'Outstanding Ownership (%)',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: OUTSTANDING_OWNERSHIP_QUERY,
  },
  {
    field: 'dilutedSharesPercent',
    type: FilterFieldType.Percent,
    title: 'Diluted Ownership (%)',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: FULLY_DILUTED_OWNERSHIP_QUERY,
  },
  {
    field: 'boardSeats',
    type: FilterFieldType.Number,
    title: 'Board Seats',
    isSortable: true,
    align: 'right',
    aggColumn: true,
    dbField: 'COALESCE(MAX("boardSeats"."totalBoardSeats"), 0)',
  },
  {
    field: 'boardSeatsHolders',
    type: FilterFieldType.Text,
    title: 'Board Seats Holder(s)',
    isSortable: false,
    excludeFilter: true,
    excludeFullSearchFilter: true,
    align: 'left',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: '> 5% Ownership',
        filters: {
          dilutedSharesPercentMin: 5,
        },
      },
    ],
  },
] as const satisfies IFilter[]
